/**
 * Client SDK for @webdevarif/notifications
 */

import { NotificationPayload, NotificationFilters, NotificationCount } from '../types';

export interface ClientConfig {
  baseUrl: string;
  websocketUrl?: string;
  autoConnect?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  endpoints?: {
    notifications: string;
    markAsRead: string;
    markAsUnread: string;
    markAll: string;
    count: string;
  };
}

export class NotificationClientSDK {
  private config: ClientConfig;
  private ws?: WebSocket;

  constructor(config: ClientConfig) {
    this.config = {
      autoConnect: true,
      reconnectInterval: 5000,
      maxReconnectAttempts: 10,
      endpoints: {
        notifications: '/notifications',
        markAsRead: '/notifications/{id}/read',
        markAsUnread: '/notifications/{id}/unread',
        markAll: '/notifications/mark-all-read',
        count: '/notifications/stats',
      },
      ...config,
    };

    if (this.config.autoConnect && this.config.websocketUrl) {
      this.connect();
    }
  }

  private async connect(): Promise<void> {
    if (!this.config.websocketUrl) return;

    try {
      this.ws = new WebSocket(this.config.websocketUrl);
      
      this.ws.onopen = () => {
        console.log('WebSocket connected');
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        // Auto-reconnect logic could go here
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }

  private handleMessage(data: any): void {
    // Handle incoming WebSocket messages
    if (data.event === 'notification:created') {
      // Emit event for React hooks to listen to
      window.dispatchEvent(new CustomEvent('notification:created', { detail: data.data }));
    }
  }

  async getNotifications(filters: NotificationFilters = {}): Promise<{ success: boolean; data?: NotificationPayload[]; total?: number; error?: string }> {
    try {
      const params = new URLSearchParams();
      if (filters.recipientId) params.append('recipientId', filters.recipientId.toString());
      if (filters.read !== undefined) params.append('read', filters.read.toString());
      if (filters.type) params.append('type', filters.type);
      if (filters.category) params.append('category', filters.category);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.offset) params.append('offset', filters.offset.toString());

      const response = await fetch(`${this.config.baseUrl}${this.config.endpoints!.notifications}?${params.toString()}`);
      return await response.json();
    } catch (error) {
      return { success: false, error: 'Failed to fetch notifications' };
    }
  }

  async markAsRead(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const endpoint = this.config.endpoints!.markAsRead.replace('{id}', id);
      const response = await fetch(`${this.config.baseUrl}${endpoint}`, { method: 'PATCH' });
      return await response.json();
    } catch (error) {
      return { success: false, error: 'Failed to mark notification as read' };
    }
  }

  async markAsUnread(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const endpoint = this.config.endpoints!.markAsUnread.replace('{id}', id);
      const response = await fetch(`${this.config.baseUrl}${endpoint}`, { method: 'PATCH' });
      return await response.json();
    } catch (error) {
      return { success: false, error: 'Failed to mark notification as unread' };
    }
  }

  async markAllAsRead(recipientId?: string | number, category?: string): Promise<{ success: boolean; updatedCount?: number; error?: string }> {
    try {
      const params = new URLSearchParams();
      if (recipientId) params.append('recipientId', recipientId.toString());
      if (category) params.append('category', category);

      const response = await fetch(`${this.config.baseUrl}${this.config.endpoints!.markAll}?${params.toString()}`, { method: 'PATCH' });
      return await response.json();
    } catch (error) {
      return { success: false, error: 'Failed to mark all notifications as read' };
    }
  }

  async deleteNotification(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.config.baseUrl}${this.config.endpoints!.notifications}/${id}`, { method: 'DELETE' });
      return await response.json();
    } catch (error) {
      return { success: false, error: 'Failed to delete notification' };
    }
  }

  async getNotificationStats(): Promise<{ success: boolean; data?: NotificationCount; error?: string }> {
    try {
      const response = await fetch(`${this.config.baseUrl}${this.config.endpoints!.count}`);
      return await response.json();
    } catch (error) {
      return { success: false, error: 'Failed to fetch notification stats' };
    }
  }

  close(): void {
    if (this.ws) {
      this.ws.close();
    }
  }
}
